import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Checkbox } from 'primereact/checkbox';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { handleAxiosError } from 'utilities/apiUtils';
import '../../../styles/FileExplorer.css';
import '../../../styles/FileUploadBox.css';
import 'primeicons/primeicons.css';

const fetchFiles = async (id: string, folderId?: string, navigate?: any) => {
  const params: Record<string, string> = { insuredId: id };
  if (folderId) params.folderId = folderId;
  const query = new URLSearchParams(params).toString();
  const url = `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insured-files?${query}`;
  try {
    const response = await axios.get(url, {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('igtoken')}`,
        'NCToken': localStorage.getItem('NCToken') || '',
      },
    });
    return response.data;
  } catch (err) {
    if (navigate) handleAxiosError(err, navigate);
  }
};

export type UploadedFile = { name: string; fileId: string; downloadUrl?: string };

export type Step5Props = {
  combinePdf: boolean;
  setCombinePdf: (v: boolean) => void;
  insuredDatabaseId: string;
  uploadedFiles: UploadedFile[];
  setUploadedFiles: (files: UploadedFile[]) => void;
  onUploadingChange?: (uploading: boolean) => void;
  masterCertificateId?: string;
  masterCertificateName?: string;
  recipientEmail?: string;
  sessionId: string;
};
type FileOrFolder = {
  id: string;
  name: string;
  type: 'folder' | 'file';
  description?: string;
  size?: string;
  uploader?: string;
  created?: string;
  lastModified?: string;
  children?: FileOrFolder[];
  downloadUrl?: string;
  parentFolderId?: string;
};


const Step5: React.FC<Step5Props> = ({ combinePdf, setCombinePdf, insuredDatabaseId, uploadedFiles, setUploadedFiles, onUploadingChange, masterCertificateId, masterCertificateName, recipientEmail, sessionId }) => {
  const navigate = useNavigate();
  const [openPathIds, setOpenPathIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFileToApi = async (file: File) => {
    setUploading(true);
    onUploadingChange?.(true);
    setUploadError(null);
    try {
      const ncToken = localStorage.getItem('NCToken');
      const igtoken = localStorage.getItem('igtoken');
      const formData = new FormData();
      formData.append('insuredId', insuredDatabaseId);
      formData.append('fileName', file.name);
      formData.append('file', file);
      if (masterCertificateId) formData.append('masterCertificateId', masterCertificateId);
      if (masterCertificateName) formData.append('masterCertificateName', masterCertificateName);
      if (recipientEmail) formData.append('recipientEmail', recipientEmail);
      formData.append('sessionId', sessionId);
      const response = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/upload-to-ams`, {
        method: 'POST',
        headers: {
          'NCToken': ncToken || '',
          'Authorization': `Bearer ${igtoken || ''}`
        },
        body: formData
      });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'Upload failed');
      setUploadedFiles([...uploadedFiles, { name: data.name || file.name, fileId: data.fileId || '', downloadUrl: data.downloadUrl }]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFileToApi(e.dataTransfer.files[0]);
    }
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => { e.preventDefault(); };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadFileToApi(e.target.files[0]);
    }
  };
  const removeFile = (idx: number) => {
    setUploadedFiles(uploadedFiles.filter((_, i) => i !== idx));
  };

  // Missing state declarations
  const [currentFolder, setCurrentFolder] = useState<FileOrFolder | null>(null);
  const [rootFolder, setRootFolder] = useState<FileOrFolder | null>(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableData, setTableData] = useState<FileOrFolder[]>([]);
  const [fetchError, setFetchError] = useState<string>("");
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [folderHistory, setFolderHistory] = useState<FileOrFolder[]>([]);

  // Fetch root folder when switching to AMS tab
  React.useEffect(() => {
    if (activeTab === 1 && insuredDatabaseId && !rootFolder) {
      setTableLoading(true);
      fetchFiles(insuredDatabaseId, undefined, navigate)
        .then((data: any) => {
          const filesData = data?.data || data;
          const root: FileOrFolder = {
            id: filesData.currentFolderId || insuredDatabaseId,
            name: filesData.name || "Root",
            type: "folder",
            children: (filesData.files || []).map((f: any) => ({
              id: f.databaseId,
              name: f.name,
              type: f.fileOrFolder === 'Folder' ? "folder" : "file",
              description: f.fileOrFolder,
              size: f.size?.toString() || "-",
              uploader: f.creatorName || "-",
              created: f.createDate || "-",
              lastModified: f.changeDate || "-",
              downloadUrl: f.downloadUrl,
              children: f.fileOrFolder === 'Folder' ? [] : undefined,
              parentFolderId: f.parentId || filesData.currentFolderId || insuredDatabaseId,
            })),
          };
          setRootFolder(root);
          setCurrentFolder(root);
          setTableData(root.children || []);
          setOpenPathIds([root.id]);
          setFolderHistory([root]);
          setHistoryIndex(0);
          setTableLoading(false);
        })
        .catch(() => {
          setTableLoading(false);
        });
    }
  }, [activeTab, insuredDatabaseId]);
  const openFolder = (folder: FileOrFolder, childrenOverride?: FileOrFolder[]) => {
    const children = childrenOverride ?? folder.children ?? [];
    const folderWithChildren: FileOrFolder = { ...folder, children };
    setCurrentFolder(folderWithChildren);
    setTableData(children);
    if (rootFolder) {
      const ids: string[] = [];
      const findPath = (node: FileOrFolder): boolean => {
        ids.push(node.id);
        if (node.id === folder.id) return true;
        if (node.children) {
          for (const child of node.children) {
            if (child.type === 'folder' && findPath(child)) return true;
          }
        }
        ids.pop();
        return false;
      };
      findPath(rootFolder);
      setOpenPathIds(ids);
    }
    setFolderHistory((prevHistory) => {
      const base = historyIndex >= 0 ? prevHistory.slice(0, historyIndex + 1) : [];
      const existingIndex = base.findIndex((f) => f.id === folder.id);
      if (existingIndex !== -1) {
        setHistoryIndex(existingIndex);
        return base;
      }
      const newHistory = [...base, folderWithChildren];
      setHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
  };

  // toggleFolder function (unchanged)
  const toggleFolder = (item: FileOrFolder) => {
    if (currentFolder && currentFolder.id === item.id) return;
    if (item.children && item.children.length === 0 && item.type === "folder" && insuredDatabaseId) {
      setTableLoading(true);
      fetchFiles(insuredDatabaseId, item.id, navigate)
        .then((data: any) => {
          const newChildren: FileOrFolder[] = data.data.files.map((f: any) => ({
            id: f.databaseId,
            name: f.name,
            type: f.fileOrFolder === 'Folder' ? "folder" : "file",
            description: f.fileOrFolder,
            size: f.size?.toString() || "-",
            uploader: f.creatorName || "-",
            created: f.createDate || "-",
            lastModified: f.changeDate || "-",
            downloadUrl: f.downloadUrl,
            children: f.fileOrFolder === 'Folder' ? [] : undefined,
            parentFolderId: item.id,
          }));
          const updateChildren = (folder: FileOrFolder): FileOrFolder => {
            if (folder.id === item.id) {
              return { ...folder, children: newChildren };
            }
            if (folder.children) {
              return { ...folder, children: folder.children.map(updateChildren) };
            }
            return folder;
          };
          setRootFolder((prev) => (prev ? updateChildren(prev) : prev));
          openFolder(item, newChildren);
          setTableLoading(false);
        })
        .catch(() => {
          setTableLoading(false);
        });
    } else if (item.type === "folder") {
      openFolder(item);
    }
  };

  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex >= 0 && historyIndex < folderHistory.length - 1;
  const handleHistoryBack = () => {
    if (!canGoBack) return;
    const newIndex = historyIndex - 1;
    const folder = folderHistory[newIndex];
    if (!folder) return;
    setHistoryIndex(newIndex);
    setCurrentFolder(folder);
    setTableData(folder.children || []);
  };
  const handleHistoryForward = () => {
    if (!canGoForward) return;
    const newIndex = historyIndex + 1;
    const folder = folderHistory[newIndex];
    if (!folder) return;
    setHistoryIndex(newIndex);
    setCurrentFolder(folder);
    setTableData(folder.children || []);
  };

  const pathSegments = React.useMemo(() => {
    if (!rootFolder) return [] as FileOrFolder[];
    const targetId = currentFolder?.id || rootFolder.id;
    const path: FileOrFolder[] = [];
    const dfs = (node: FileOrFolder, acc: FileOrFolder[]): boolean => {
      const nextAcc = [...acc, node];
      if (node.id === targetId) {
        path.push(...nextAcc);
        return true;
      }
      if (node.children) {
        for (const child of node.children) {
          if (dfs(child, nextAcc)) return true;
        }
      }
      return false;
    };
    dfs(rootFolder, []);
    return path.length ? path : [rootFolder];
  }, [rootFolder, currentFolder]);

  const trimName = (name: string, max = 30) =>
    name.length > max ? name.slice(0, max - 2) + '…' : name;

  const renderTreeItem = (item: FileOrFolder, level: number = 0) => {
    const isExpanded = openPathIds.includes(item.id);
    const isSelected = currentFolder && currentFolder.id === item.id;
    if (item.type === "folder") {
      return (
        <div>
          <button
            className={`file-explorer-folder-btn${isSelected ? ' active' : ''}`}
            style={{ paddingLeft: `${level * 18 + 12}px` }}
            onClick={() => toggleFolder(item)}
            type="button"
            title={item.name}
          >
            <i className={`pi ${isExpanded ? "pi-chevron-down" : "pi-chevron-right"}`} />
            <i className="pi pi-folder" />
            <span className="file-explorer-folder-name">{trimName(item.name, 32)}</span>
          </button>
          {isExpanded && item.children && item.children.filter(child => child.type === 'folder').length > 0 && (
            <div className="file-explorer-folder-children">
              {item.children.filter(child => child.type === 'folder').map(child => renderTreeItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="page">
      <div className="page__sectionHeader">
        <div>
          <h2 className="page__title">Attach Files</h2>
          <p className="page__muted">Attach documents for this certificate</p>
        </div>
      </div>
      <div className="file-explorer-combine-row" style={{ marginBottom: '1.2rem' }}>
        <Checkbox inputId="combinePdf" checked={combinePdf} onChange={e => setCombinePdf(e.checked ?? false)} />
        <label htmlFor="combinePdf" className="file-explorer-combine-label">
          Combine all PDFs into one
        </label>
      </div>
      <TabView activeIndex={activeTab} onTabChange={e => setActiveTab(e.index)} className="file-upload-tabview">
        <TabPanel header="Upload File">
              <div className="file-upload-box-section" style={{ minHeight: '260px', justifyContent: 'center' }}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept=".pdf,.txt,.docx,.md"
            />

            {/* ── Uploading state ── */}
            {uploading && (
              <div className="file-upload-box file-upload-box--active" style={{ cursor: 'default' }}>
                <div className="fileUp__loader">
                  <div className="fileUp__spinnerWrap">
                    <span className="fileUp__ring" />
                    <span className="fileUp__spinnerIcon"><i className="pi pi-upload" /></span>
                  </div>
                  <div className="fileUp__loaderTitle">Uploading your file…</div>
                  <div className="fileUp__loaderSub">Please wait, do not close this window</div>
                  <div className="fileUp__loaderDots">
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}

            {/* ── Uploaded files list ── */}
            {uploadedFiles.length > 0 && (
              <div style={{ width: '100%', maxWidth: 600, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {uploadedFiles.map((info, idx) => {
                  const ext = (info.name || '').split('.').pop()?.toLowerCase();
                  const iconClass = ext === 'pdf' ? 'pi-file-pdf' : ext === 'docx' || ext === 'doc' ? 'pi-file-word' : 'pi-file';
                  return (
                    <div key={idx} className="fileUp__card">
                      <div className="fileUp__cardIcon">
                        <i className={`pi ${iconClass}`} />
                      </div>
                      <div className="fileUp__cardBody">
                        <div className="fileUp__cardName" title={info.name}>{info.name || 'Uploaded file'}</div>
                        <div className="fileUp__cardMeta">
                          <span className="fileUp__cardBadge">
                            <i className="pi pi-check-circle" /> Uploaded successfully
                          </span>
                          {info.downloadUrl && (
                            <a href={info.downloadUrl} target="_blank" rel="noopener noreferrer" className="fileUp__cardDownload">
                              <i className="pi pi-download" /> Download
                            </a>
                          )}
                        </div>
                      </div>
                      <button type="button" className="fileUp__cardRemove" title="Remove file" onClick={() => removeFile(idx)}>
                        <i className="pi pi-times" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Drop zone — always visible to add more files ── */}
            {!uploading && (
              <div
                className="file-upload-box"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
                style={{ minHeight: uploadedFiles.length > 0 ? 100 : 160 }}
              >
                <div className="fileUp__emptyIcon">
                  <i className="pi pi-cloud-upload" />
                </div>
                <div className="file-upload-text">
                  {uploadedFiles.length > 0 ? 'Add another file' : 'Drag & drop your file here'}
                </div>
                <div className="file-upload-subtext">PDF, TXT, DOCX, MD &bull; Max 50 MB</div>
                <div className="fileUp__clickHint">
                  <i className="pi pi-mouse" /> or click to browse
                </div>
              </div>
            )}

            {/* ── Upload error ── */}
            {uploadError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#b91c1c', fontSize: 13, marginTop: 4 }}>
                <i className="pi pi-exclamation-circle" />
                {uploadError}
              </div>
            )}
          </div>
        </TabPanel>
        {/* Select from AMS tab hidden
        <TabPanel header="Select from AMS">
          <div style={{ width: '100%'}}>
            <div className="file-explorer-path-bar">
              <button className="file-explorer-nav-btn" onClick={handleHistoryBack} disabled={!canGoBack} title="Back">
                <i className="pi pi-arrow-left" />
              </button>
              <button className="file-explorer-nav-btn" onClick={handleHistoryForward} disabled={!canGoForward} title="Forward">
                <i className="pi pi-arrow-right" />
              </button>
              {pathSegments.map((seg, idx, arr) => (
                <React.Fragment key={seg.id || 'root'}>
                  {idx > 0 && <span className="file-explorer-path-separator">/</span>}
                  <span
                    className="file-explorer-path-segment"
                    onClick={() => idx < arr.length - 1 && openFolder(seg)}
                    title={seg.name}
                  >
                    {trimName(seg.name, 35)}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="file-explorer-container">
            <div className="file-explorer-sidebar">
              <div className="file-explorer-sidebar-title">Folders</div>
              <div>
                {rootFolder && renderTreeItem(rootFolder)}
              </div>
            </div>
            <div className="file-explorer-main">
              <div className="file-explorer-files-title">Files</div>
              <div className="file-explorer-files-list">
                {tableLoading ? (
                  <div className="file-explorer-loader file-explorer-loader-center"><i className="pi pi-spin pi-spinner" /></div>
                ) : fetchError ? (
                  <div className="file-explorer-error">{fetchError}</div>
                ) : tableData.length === 0 ? (
                  <div className="file-explorer-no-files">No files in this folder.</div>
                ) : (
                  tableData.filter(file => file.type === 'file').map(file => {
                    const isSelected = selectedAMSFile && selectedAMSFile.databaseId === file.id;
                    return (
                      <div
                        key={file.id}
                        className={`file-explorer-file-card${isSelected ? ' selected' : ''}`}
                        title={file.name}
                        onClick={() => {
                          setSelectedAMSFile({
                            databaseId: file.id,
                            name: file.name,
                            downloadUrl: file.downloadUrl
                          });
                          if (onFileSelected) onFileSelected(file.id);
                          setUploadedFileInfo(null);
                        }}
                      >
                        <i className="pi pi-file" />
                        <span className="file-explorer-file-name">{file.name}</span>
                        <i className="pi pi-check-circle file-explorer-file-check" />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </TabPanel>
        */}
      </TabView>
    </div>
  );
};

export default Step5;
